// RasPage — User-RAS-Rules (eve_ras_memories) View · Add · Delete.

import { useEffect, useState } from 'react';
import { Plus, Shield, Trash2 } from 'lucide-react';
import { useEveMemory } from '@/hooks/use-eve-memory';
import { useAuth } from '@/hooks/use-auth';

export function RasPage() {
  const {
    rasRules,
    globalRasRules,
    loadRasRules,
    loadGlobalRasRules,
    saveRasRule,
    deleteRasRule,
    deleteGlobalRasRule,
    clearRasRules,
  } = useEveMemory();
  const { isOwner } = useAuth();
  const [adding, setAdding] = useState(false);
  const [draftRule, setDraftRule] = useState('');
  const [draftContext, setDraftContext] = useState('');
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'mine' | 'global'>('mine');

  useEffect(() => {
    void loadRasRules(true);
    void loadGlobalRasRules(true);
  }, [loadRasRules, loadGlobalRasRules]);

  async function handleAdd() {
    if (draftRule.trim().length === 0 || saving) return;
    setSaving(true);
    setError(null);
    try {
      await saveRasRule(draftRule.trim(), draftContext.trim());
      setDraftRule('');
      setDraftContext('');
      setAdding(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string | number) {
    if (!window.confirm('Diese RAS-Regel wirklich löschen?')) return;
    try {
      await deleteRasRule(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Löschen fehlgeschlagen');
    }
  }

  async function handleDeleteGlobal(id: string | number) {
    if (!window.confirm('Diese globale RAS-Regel wirklich löschen?')) return;
    try {
      await deleteGlobalRasRule(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Löschen fehlgeschlagen');
    }
  }

  async function handleClearAll() {
    if (rasRules.length === 0) return;
    if (!window.confirm(`Wirklich ALLE ${rasRules.length} eigenen RAS-Regeln löschen? Nicht rückgängig.`)) return;
    try {
      await clearRasRules();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Clear fehlgeschlagen');
    }
  }

  const mineFiltered = rasRules.filter((r) =>
    query.trim().length === 0 ? true : r.rule.toLowerCase().includes(query.toLowerCase()),
  );
  const globalFiltered = globalRasRules.filter((r) =>
    query.trim().length === 0 ? true : r.rule.toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <div className="h-full overflow-y-auto p-5">
      <div className="mx-auto max-w-3xl space-y-4">
        <header className="flex items-center justify-between border-b border-white/5 pb-4">
          <div className="flex items-center gap-3">
            <Shield className="size-5 text-amber-400" />
            <div>
              <h1 className="text-xl font-bold">RAS-Regeln</h1>
              <p className="mt-0.5 text-[12px] text-neutral-500">
                {rasRules.length} eigene + {globalRasRules.length} globale — EVE's Reflektionsmuster.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setAdding((s) => !s)}
              className="inline-flex items-center gap-1 rounded-lg border border-amber-500/30 bg-amber-500/15 px-3 py-1.5 text-[12px] font-bold text-amber-300 hover:bg-amber-500/25"
            >
              <Plus className="size-3" />
              Neue Regel
            </button>
            {rasRules.length > 0 ? (
              <button
                type="button"
                onClick={() => void handleClearAll()}
                className="rounded-lg border border-rose-500/20 px-2.5 py-1.5 text-[11px] text-rose-400/80 hover:bg-rose-500/10"
              >
                Alle eigenen löschen
              </button>
            ) : null}
          </div>
        </header>

        {error ? (
          <div className="rounded border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-300">
            {error}
          </div>
        ) : null}

        {adding ? (
          <div className="glass-strong space-y-2 rounded-2xl p-4">
            <textarea
              autoFocus
              value={draftRule}
              onChange={(e) => setDraftRule(e.target.value)}
              rows={2}
              placeholder='z.B. „Bei niedrigem Volumen immer Wickliquid-Sweeps abwarten" …'
              className="w-full rounded-lg border border-white/10 bg-neutral-900/60 px-3 py-2 text-[13px] outline-none focus:border-amber-500/40"
            />
            <input
              type="text"
              value={draftContext}
              onChange={(e) => setDraftContext(e.target.value)}
              placeholder="Trigger-Kontext (optional)"
              className="w-full rounded-lg border border-white/10 bg-neutral-900/60 px-3 py-2 text-[12px] outline-none focus:border-amber-500/40"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setAdding(false);
                  setDraftRule('');
                  setDraftContext('');
                }}
                className="rounded-lg border border-white/10 px-3 py-1.5 text-[12px] text-neutral-300 hover:bg-white/5"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => void handleAdd()}
                disabled={draftRule.trim().length === 0 || saving}
                className="rounded-lg bg-amber-500 px-3 py-1.5 text-[12px] font-bold text-neutral-950 hover:bg-amber-400 disabled:bg-neutral-700 disabled:text-neutral-500"
              >
                {saving ? 'Speichert…' : 'Speichern'}
              </button>
            </div>
          </div>
        ) : null}

        <div className="flex items-center gap-1 border-b border-white/5">
          <button
            type="button"
            onClick={() => setTab('mine')}
            className={`px-3 py-1.5 text-[12px] ${tab === 'mine' ? 'border-b-2 border-amber-500 font-bold text-amber-300' : 'text-neutral-400 hover:text-neutral-200'}`}
          >
            Eigene ({rasRules.length})
          </button>
          <button
            type="button"
            onClick={() => setTab('global')}
            className={`px-3 py-1.5 text-[12px] ${tab === 'global' ? 'border-b-2 border-amber-500 font-bold text-amber-300' : 'text-neutral-400 hover:text-neutral-200'}`}
          >
            Global ({globalRasRules.length})
          </button>
        </div>

        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Regeln filtern…"
          className="w-full rounded-lg border border-white/10 bg-neutral-900/60 px-3 py-2 text-[12px] outline-none focus:border-amber-500/40"
        />

        {tab === 'mine' ? (
          mineFiltered.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/10 p-12 text-center text-[12px] text-neutral-500">
              {rasRules.length === 0
                ? 'Noch keine eigenen RAS-Regeln. EVE extrahiert sie automatisch aus euren Gesprächen.'
                : 'Keine Regeln passend zum Filter.'}
            </div>
          ) : (
            <ul className="space-y-2">
              {mineFiltered.map((r) => (
                <li
                  key={r.id}
                  className="glass flex items-start gap-3 rounded-xl border border-white/5 p-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] leading-relaxed text-neutral-200">{r.rule}</p>
                    {r.trigger_context ? (
                      <p className="mt-1 text-[11px] italic text-neutral-500">when: {r.trigger_context}</p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleDelete(r.id)}
                    aria-label="Regel löschen"
                    className="shrink-0 text-neutral-500 hover:text-rose-400"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )
        ) : globalFiltered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 p-12 text-center text-[12px] text-neutral-500">
            Keine globalen Regeln passend zum Filter.
          </div>
        ) : (
          <ul className="space-y-2">
            {globalFiltered.map((r) => (
              <li
                key={r.id}
                className="glass flex items-start gap-3 rounded-xl border border-amber-500/15 bg-amber-500/[0.03] p-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] leading-relaxed text-neutral-200">{r.rule}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-neutral-500">
                    {r.category ? (
                      <span className="rounded bg-neutral-800/60 px-1.5 py-0.5">{r.category}</span>
                    ) : null}
                    <span>prio {r.priority ?? 100}</span>
                    {r.trigger_context ? <span className="italic">when: {r.trigger_context}</span> : null}
                  </div>
                </div>
                {isOwner ? (
                  <button
                    type="button"
                    onClick={() => void handleDeleteGlobal(r.id)}
                    aria-label="Globale Regel löschen"
                    className="shrink-0 text-neutral-500 hover:text-rose-400"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
